// Lo que te pusiste.
//
// No es un registro burocrático: es la única fuente de verdad sobre tu
// uso real, y es lo que hace que "primero lo que hace más tiempo no
// usás" signifique algo. Todo lo demás en la app son datos que declarás;
// esto es lo que pasó.
//
// Por eso se puede BORRAR un registro, y desde el 21/9 también
// CORREGIRLO (pedido del dueño). Un uso mal cargado —la camisa
// equivocada, el día equivocado— ensucia la frescura de varias prendas
// a la vez. Antes decía acá que editar "sería inventar historia"; no lo
// es: corregir un error de carga deja la historia MÁS cierta, y la base
// recalcula el uso sola (triggers de supabase/uso_por_trigger.sql).
//
// «Editar» abre el registro en Armar a mano y al guardar corrige ESE
// registro —prendas, fecha, ocasión—, no crea otro.

import { useCallback, useEffect, useState } from "react";
import * as api from "./api";
import Visor from "./Visor";
import Calendario from "./Calendario";
import { haceCuanto, hoyLocal, aISO } from "./fechas";
import { fotoChica, fotoGrande } from "./foto";
import { t, tCat, tError, tPlural, useIdioma, idiomaActual, fecha as fechaEnIdioma, numero } from "./i18n";
import { IconoFichada } from "./Iconos";
import { PerfumeDelRegistro } from "./FrascoBoton";

export default function Historial({ recargarToken, onEditarOutfit }) {
  /* Se repinta cuando llega el idioma. Sin esto el componente depende
   * de que su padre se repinte por otra razón — hoy pasa, y por eso el
   * bug del 2026-08-25 se veía en unas pantallas y no en otras. */
  useIdioma();
  const [registros, setRegistros] = useState(null);
  const [error, setError] = useState("");
  const [cargando, setCargando] = useState(true);
  const [ampliada, setAmpliada] = useState(null);

  // Lista o calendario. Las dos vistas usan EXACTAMENTE los mismos
  // datos: `listarOutfits()` se pide una sola vez y cambiar de vista no
  // vuelve a pedir nada. Por eso el interruptor es instantáneo y por eso
  // vale tenerlo — si cada cambio costara una consulta, sería mejor
  // elegir una sola vista y bancarla.
  const [vista, setVista] = useState("lista");

  const traer = useCallback(async () => {
    try {
      setError("");
      setRegistros(await api.listarOutfits());
    } catch (e) {
      setError(tError(e));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    traer();
  }, [traer, recargarToken]);

  // Repetir: registrar las mismas prendas CON FECHA DE HOY.
  //
  // No es optimista como el borrado. Un borrado que falla se deshace
  // solo y no dejó nada; un registro que falla y se mostró como hecho
  // te haría creer que el uso quedó anotado — y el uso es la única
  // fuente de verdad de toda la app (de ahí sale "hace cuánto no usás
  // esto"). Acá vale esperar la confirmación del servidor.
  //
  // Después se vuelve a pedir el historial: el día de hoy tiene un
  // outfit más y el calendario tiene que mostrarlo enseguida, o el
  // botón parecería no haber hecho nada.
  async function repetir(prendaIds) {
    await api.registrarOutfit({
      prenda_ids: prendaIds,
      usada_en: aISO(hoyLocal()),
    });
    await traer();
  }

  async function borrar(id) {
    // Optimista, igual que el resto de la app. Un borrado de historial
    // no vale una espera.
    const antes = registros;
    setRegistros((r) => r.filter((o) => o.id !== id));
    try {
      await api.borrarOutfit(id);
    } catch (e) {
      setError(tError(e));
      setRegistros(antes);
    }
  }

  /* Fichar todas las prendas de una salida.
   *
   * ⚠️  LA MARCA ES DE LA PRENDA, NO DE LA SALIDA (decisión de producto,
   *     ver supabase/fichada.sql). O sea que fichar desde acá también
   *     cambia cómo se ve esa prenda en las OTRAS salidas donde
   *     aparece. Es lo que se quiso: la pregunta es "¿esta ya se vio?",
   *     y la respuesta no depende de por dónde la mires.
   *
   * Por eso se actualizan todos los registros a la vez y no solo el que
   * tocaste: si la misma camisa está en tres salidas, las tres tienen
   * que cambiar en pantalla. Sin esto, dos filas de la misma lista
   * mostrarían estados distintos de la misma prenda. */
  async function fichar(outfit) {
    const vivas = (outfit.prendas || []).filter((p) => p.activa !== false);
    const nueva = !(vivas.length > 0 && vivas.every((p) => p.fichada === true));
    const ids = new Set(vivas.map((p) => p.id));
    const antes = registros;

    const aplicar = (valor) =>
      setRegistros((r) =>
        (r || []).map((o) => ({
          ...o,
          prendas: (o.prendas || []).map((p) =>
            ids.has(p.id) ? { ...p, fichada: valor } : p
          ),
        }))
      );

    aplicar(nueva); // optimista
    try {
      await api.ficharOutfit(outfit.id, nueva);
    } catch (e) {
      setError(tError(e));
      setRegistros(antes);
    }
  }

  if (cargando) return <p className="tenue">{t("historial.cargando", "Cargando el historial...")}</p>;
  if (error && !registros) return <div className="mensaje-error" role="alert">{error}</div>;

  if (!registros?.length) {
    return (
      <p className="tenue">
        {t("historial.vacioAntes", 'Todavía no registraste ningún outfit. Cuando le des a "Me lo puse" en')}{" "}
        <strong>{t("app.tabOutfits", "Qué me pongo")}</strong>
        {t("historial.vacioDespues", ", aparecen acá — y a partir de ahí las sugerencias empiezan a priorizar lo que hace más tiempo no usas.")}
      </p>
    );
  }

  // Agrupado por mes. Un historial plano de 120 filas no se lee; con
  // los meses arriba se navega de un vistazo.
  const meses = agrupar(registros);

  return (
    <section>
      <div className="barra-lote">
        <div>
          <strong>{numero(registros.length)}</strong>{" "}
          {tPlural("plural.outfitsRegistrados", registros.length)}
        </div>

        <div className="conmutador">
          <button
            className={`conmutador-opcion ${vista === "lista" ? "conmutador-activa" : ""}`}
            onClick={() => setVista("lista")}
          >
            {t("historial.vistaLista", "Lista")}
          </button>
          <button
            className={`conmutador-opcion ${vista === "mes" ? "conmutador-activa" : ""}`}
            onClick={() => setVista("mes")}
          >
            {t("historial.vistaCalendario", "Calendario")}
          </button>
        </div>
      </div>

      {error && <div className="mensaje-error" role="alert">{error}</div>}

      {vista === "mes" ? (
        <Calendario
          registros={registros}
          onBorrar={borrar}
          onRepetir={repetir}
          onEditar={onEditarOutfit}
          onAmpliar={(p) =>
            fotoGrande(p) && setAmpliada({ url: fotoGrande(p), alt: p.nombre, prenda: p })
          }
        />
      ) : (
        meses.map(([mes, items]) => (
          <div key={mes} className="mes">
            <p className="filtro-titulo mes-titulo">{mes}</p>
            {items.map((o) => (
              <Registro
                key={o.id}
                outfit={o}
                onBorrar={() => borrar(o.id)}
                onFichar={() => fichar(o)}
                onEditar={() => onEditarOutfit?.(o)}
                onAmpliar={(p) =>
                  fotoGrande(p) && setAmpliada({ url: fotoGrande(p), alt: p.nombre, prenda: p })
                }
              />
            ))}
          </div>
        ))
      )}

      <Visor
        url={ampliada?.url}
        alt={ampliada?.alt}
        prenda={ampliada?.prenda}
        onCerrar={() => setAmpliada(null)}
      />
    </section>
  );
}

function Registro({ outfit, onBorrar, onFichar, onEditar, onAmpliar }) {
  /* El botón se ve encendido solo si TODAS las prendas de la salida
   * están fichadas. Con "alguna" quedaría encendido para siempre desde
   * la primera, y no habría forma de saber si falta alguna.
   *
   * Las prendas borradas del armario no cuentan: siguen en el historial
   * pero ya no se pueden actualizar, así que exigirlas dejaría el botón
   * apagado para siempre en las salidas viejas. */
  const vivas = (outfit.prendas || []).filter((p) => p.activa !== false);
  const todasFichadas = vivas.length > 0 && vivas.every((p) => p.fichada === true);

  const fecha = new Date(outfit.usada_en);

  return (
    <article className="registro">
      <div className="registro-fecha">
        <span className="registro-dia">{numero(fecha.getDate())}</span>
        <span className="tenue texto-mini">
          {fechaEnIdioma(fecha, { weekday: "short" })}
        </span>
      </div>

      {/* ⚠️  TODO LO QUE NO ES LA FECHA VA ACÁ ADENTRO, Y NO AL LADO.
        *
        * Antes la tarjeta eran TRES columnas —fecha, fotos, texto— y en
        * un celular de 390px al texto le quedaban 108px: los nombres de
        * las prendas caían de a una o dos palabras por renglón y una
        * sola tarjeta medía más de mil píxeles de alto. Los tres botones
        * también se apilaban, cada uno en su renglón.
        *
        * Con las fotos y el texto APILADOS dentro de una sola columna,
        * los dos usan el ancho entero: las fotos crecieron a 56px, los
        * nombres entran en un renglón y los botones en otro. La misma
        * tarjeta mide ~175px. */}
      <div className="registro-cuerpo">
      <p className="registro-meta texto-mini">
        {haceCuanto(outfit.usada_en, { idioma: idiomaActual() })}
        {outfit.ocasion && (
          <span className="tenue"> · {tCat("ocasion", outfit.ocasion)}</span>
        )}
        {outfit.origen === "favorito" && (
          <span className="tenue"> · {t("historial.guardado", "guardado")}</span>
        )}
        {/* El perfume de ese día (22/9) viaja en ESTE renglón y no en uno
            propio: es un dato del día, igual que la ocasión, y en su
            propio renglón costaba 18px por tarjeta a cambio de nada. */}
        <PerfumeDelRegistro perfume={outfit.perfume} enLinea />
      </p>

      <div className="registro-prendas">
        {outfit.prendas.map((p) => (
          <div key={p.id} className="registro-prenda" title={p.nombre}>
            {fotoChica(p) ? (
              <img loading="lazy" decoding="async"
                src={fotoChica(p)}
                alt={p.nombre}
                className="prenda-foto"
                onClick={() => onAmpliar(p)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  /* Enter y espacio hacen lo mismo que el click, sea lo que
                     sea. No se duplica la expresión del onClick: se dispara
                     el click de verdad. Copiar el handler exigía parsear JSX
                     con llaves anidadas, que es justamente lo que este
                     proyecto ya aprendió a no hacer. */
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.currentTarget.click();
                  }
                }}
              />
            ) : (
              <div className="prenda-foto prenda-sin-foto">
                <span className="tenue texto-mini">
                  {p.activa === false
                    ? t("historial.borrada", "borrada")
                    : t("historial.sinFoto", "sin foto")}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ⚠️  SOLO EN FAVORITOS, Y SOLO AVISA.
          *
          * Un favorito es una plantilla para volver a usar. Si editaste
          * una prenda y el conjunto dejó de ser armable —se quedó sin
          * una zona, o las estaciones ya no se cruzan— hasta ahora se
          * seguía viendo igual, como si estuviera en pie.
          *
          * No se oculta ni se corrige: se dice qué pasó y decidís vos.
          * Un favorito que desaparece solo es el error que este
          * proyecto evita en todos lados — avisar es accionable, el
          * silencio no.
          *
          * El historial (`elegido`) NO se marca nunca: te pusiste eso
          * ese martes, y eso no cambia porque hoy la prenda esté
          * clasificada distinto. El servidor ya manda `problemas`
          * vacío para esos. */}
        {(outfit.problemas || []).map((pb) => (
          <p key={pb.codigo} className="aviso-dudosa texto-chico" style={{ marginBlock: "var(--e1)" }}>
            {pb.codigo === "ZONA_FALTANTE"
              ? t("historial.rotoZona", "Ya no cubre: {zonas}. Editaste alguna prenda.", {
                  zonas: (pb.zonas || []).map((z) => tCat("zona", z)).join(", "),
                })
              : pb.codigo === "ESTACIONES_INCOMPATIBLES"
                ? t("historial.rotoEstaciones", "Estas prendas ya no comparten ninguna estación.")
                : pb.codigo === "PRENDA_NO_DISPONIBLE"
                  ? t("historial.rotoNoDisponible", "Alguna prenda no está disponible ahora.")
                  : t("historial.rotoOtro", "Este conjunto ya no se puede armar.")}
          </p>
        ))}
        {/* ⚠️  LA CLASE ES OBLIGATORIA Y NO DECORATIVA.
          *
          * El recorte a un renglón vivía en `.registro-datos p:last-of-type`.
          * El 22/9 se agregó el renglón del perfume DESPUÉS de este, el
          * selector pasó a apuntar al perfume, y los nombres se
          * desplegaron completos sin que ningún test lo viera: veinte
          * renglones en el celular. Un selector que depende de quién
          * quedó último se rompe cada vez que alguien agrega algo.
          *
          * Ahora el recorte cuelga de `.registro-nombres`, que nombra lo
          * que es. Lo cuida `test_css.js` §13. */}
        <p className="texto-mini tenue registro-nombres">
          {outfit.prendas.map((p) => p.nombre).join(" · ")}
        </p>
        {/* Fichar y Editar van ANTES de borrar, y separados: borrar es
            irreversible y no conviene que la mano lo encuentre de
            paso. */}
        <div className="registro-acciones">
          <button
            type="button"
            className={`chip ${todasFichadas ? "chip-activo" : ""}`}
            onClick={onFichar}
            aria-pressed={todasFichadas}
            title={t("historial.ficharTitulo", "Marcar las prendas de esta salida como ya mostradas")}
          >
            <IconoFichada relleno={todasFichadas} />
            {t("historial.fichar", "Fichar")}
          </button>
          <button
            type="button"
            className="boton-secundario"
            onClick={onEditar}
            title={t("historial.editarTitulo", "Corregir las prendas, la fecha o la ocasión de este registro")}
          >
            {t("historial.editar", "Editar")}
          </button>
          <button className="boton-texto registro-borrar" onClick={onBorrar}>
            {t("historial.borrarRegistro", "Borrar registro")}
          </button>
        </div>
      </div>
    </article>
  );
}

// "agosto 2026" → los registros de ese mes, en el orden que ya venían
// (más nuevo primero).
function agrupar(registros) {
  const mapa = new Map();
  for (const o of registros) {
    const d = new Date(o.usada_en);
    /* ⚠️  EN EL IDIOMA DE LA PERSONA, NO EN "es" (auditoría 2026-09-15,
     * §2.5). Un usuario en japonés veía «septiembre 2026» como título
     * de cada grupo. `test_i18n.js` 14 tiene techo cero para
     * `toLocale*("es")`. */
    const clave = fechaEnIdioma(d, { month: "long", year: "numeric" });
    if (!mapa.has(clave)) mapa.set(clave, []);
    mapa.get(clave).push(o);
  }
  return [...mapa.entries()];
}
